import { type AggRequest, type AggResponse, fetchAgg } from '../../../../common/utils/fetch-agg';
import { generateHash } from '../../../../utils/caching/common/key-generator';
import { promiseCache } from '../../../../utils/caching/persist-refresh';

import { operationName, quickFindUserDocumentsQuery } from './query';
import {
	type QuickFindUserDocumentsQueryType,
	type QuickFindUserDocumentsQueryVariables,
} from './types';

export type QuickFindUserDocumentsQueryProps = Pick<
	AggRequest<QuickFindUserDocumentsQueryVariables>,
	'aggAbsoluteUrl' | 'variables'
>;

export type CachedQuickFindUserDocumentsQueryProps = QuickFindUserDocumentsQueryProps & {
	expiration?: number | undefined;
	onlyRefreshIfExpired?: boolean | undefined;
	returnExpired?: boolean | undefined;
};

const QUICK_FIND_USER_DOCUMENTS_CACHE_KEY = 'quick-find-user-documents';
const FAILURE_EXPIRATION_MS = 5 * 1000; // 5 seconds for failure
const SUCCESS_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes for success

export const userDocumentsQuery: ({
	variables,
	aggAbsoluteUrl,
}: QuickFindUserDocumentsQueryProps) => Promise<AggResponse<QuickFindUserDocumentsQueryType>> = ({
	variables,
	aggAbsoluteUrl,
}: QuickFindUserDocumentsQueryProps) => {
	return fetchAgg<QuickFindUserDocumentsQueryVariables, QuickFindUserDocumentsQueryType>({
		variables,
		aggAbsoluteUrl,
		graphQuery: quickFindUserDocumentsQuery,
		operationName,
	});
};

const {
	refreshOnAccessDecorator: refreshOnAccessDecoratorUserDocuments,
	clear: clearUserDocumentsCache,
} = promiseCache<AggResponse<QuickFindUserDocumentsQueryType>>({
	name: QUICK_FIND_USER_DOCUMENTS_CACHE_KEY,
});

export const cachedUserDocumentsQuery: ({
	variables,
	aggAbsoluteUrl,
	expiration,
	onlyRefreshIfExpired,
	returnExpired,
}: CachedQuickFindUserDocumentsQueryProps) =>
	| AggResponse<QuickFindUserDocumentsQueryType>
	| Promise<AggResponse<QuickFindUserDocumentsQueryType>> = ({
	variables,
	aggAbsoluteUrl,
	expiration,
	onlyRefreshIfExpired,
	returnExpired,
}: CachedQuickFindUserDocumentsQueryProps) => {
	const cacheKey = generateHash(
		`${QUICK_FIND_USER_DOCUMENTS_CACHE_KEY}|${JSON.stringify(variables)}`,
	).toString();

	const writeBackWithTTL = (
		value:
			| AggResponse<QuickFindUserDocumentsQueryType>
			| Promise<AggResponse<QuickFindUserDocumentsQueryType>>,
		ttl: number,
	) => {
		refreshOnAccessDecoratorUserDocuments({
			key: cacheKey,
			supplier: async () => value,
			returnExpired: false,
			onlyRefreshIfExpired: false,
			expiration: ttl,
		});
	};

	return refreshOnAccessDecoratorUserDocuments({
		key: cacheKey,
		supplier: async () => {
			try {
				const result = await userDocumentsQuery({ variables, aggAbsoluteUrl });
				writeBackWithTTL(result, SUCCESS_EXPIRATION_MS);
				return result;
			} catch (error) {
				// On failure, cache the rejected promise with a short TTL to avoid hammering the service
				// This ensures the error is still thrown when accessed, but prevents immediate retries
				writeBackWithTTL(Promise.reject(error), FAILURE_EXPIRATION_MS);

				// Re-throw the error so the caller knows the request failed
				throw error;
			}
		},
		expiration,
		onlyRefreshIfExpired,
		returnExpired,
	});
};

export const __test_clearUserDocumentsCache: (key?: string) => void = (key?: string): void =>
	clearUserDocumentsCache(key);
